import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDateTime } from '@/shared/lib/format'
import type {
  SurveyAssignmentDto, SurveyDesignReadinessDto, SurveyInstrumentDto, SurveyLinkDto,
  SurveyResponseDto, SurveySampleSizeDto, SurveyScheduleDto,
} from '@/shared/api/types'
import {
  getDesignReadiness, getInstrument, getSampleSize, getSchedule, listAssignments,
  listSurveyLinks, listVersions, transitionPoll,
} from './api'
import {
  audienceLabel, categoryLabel, count, frequencyLabel, linkTypeLabel, methodologyLabel,
  num, pollStatusLabel, purposeLabel, questionTypeLabel, Pill,
} from './vocab'

/**
 * Step 5 — everything decided, in one place, before the poll is submitted.
 *
 * ## What this screen is for
 *
 * Not a summary for its own sake. It is the last point at which the four
 * decisions taken on separate steps can be read AGAINST each other — a target
 * of 400 against a declared ±3%, a monthly cadence against a poll in no
 * tracker, a candidate link against a scope that was widened afterwards. Each
 * of those is fine on its own step and wrong together.
 *
 * ## The transitions are the server's list
 *
 * `allowedTransitions` comes from the poll. This screen offers exactly what is
 * on it and nothing else — a hardcoded "Submit for review" button would offer
 * an edge the state machine may refuse, or hide one it would have allowed.
 */
export function ConfigReviewStep({ poll, canUpdate, onMoved }: {
  poll: SurveyResponseDto
  canUpdate: boolean
  onMoved: () => void
}) {
  const [links, setLinks] = useState<SurveyLinkDto[] | null>(null)
  const [schedule, setSchedule] = useState<SurveyScheduleDto | null>(null)
  const [sample, setSample] = useState<SurveySampleSizeDto | null>(null)
  const [sampleError, setSampleError] = useState<string | null>(null)
  const [readiness, setReadiness] = useState<SurveyDesignReadinessDto | null>(null)
  const [instrument, setInstrument] = useState<SurveyInstrumentDto | null>(null)
  const [assignments, setAssignments] = useState<SurveyAssignmentDto[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    void listSurveyLinks(poll.id).then((l) => { if (alive) setLinks(l) }).catch(() => {})
    void getSchedule(poll.id).then((s) => { if (alive) setSchedule(s) }).catch(() => {})
    void getDesignReadiness(poll.id).then((r) => { if (alive) setReadiness(r) }).catch(() => {})
    // The sample verdict REFUSES a poll with no declared margin, and that refusal
    // is a finding for this screen rather than an error to swallow.
    void getSampleSize(poll.id)
      .then((s) => { if (alive) { setSample(s); setSampleError(null) } })
      .catch((e) => { if (alive) { setSample(null); setSampleError(explain(e)) } })

    /*
      THE QUESTIONS THEMSELVES.
 
      Via `listVersions` and not `poll.currentVersion`: that field is null until
      the FIRST publish, and a review screen that showed nothing for every draft
      would be blank exactly when somebody is reviewing what they just wrote.
      The newest version is the one under review, published or not.
    */
    /*
      Staffing, because `→ LIVE` refuses a poll nobody is fielding — and Fieldwork
      is not a step in this wizard, so it is the requirement people meet last and
      by surprise. The server lists every unmet condition at once when the button
      is clicked; this puts the one that needs a different SCREEN in front of the
      click.
    */
    void listAssignments(poll.id)
      .then((rows) => { if (alive) setAssignments(rows) })
      .catch(() => { if (alive) setAssignments(null) })

    void listVersions(poll.id)
      .then((versions) => {
        const latest = [...versions].sort((a, b) => b.versionNo - a.versionNo)[0]
        return latest ? getInstrument(latest.id) : null
      })
      .then((doc) => { if (alive) setInstrument(doc) })
      .catch(() => { if (alive) setInstrument(null) })
    return () => { alive = false }
  }, [poll.id, poll.version])

  async function move(statusCode: string) {
    setBusy(true)
    setError(null)
    try {
      await transitionPoll(poll.id, statusCode)
      onMoved()
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const questions = [...(instrument?.questions ?? [])].sort((a, b) => a.position - b.position)
  // The LOADED instrument's count where there is one, so a draft that has never
  // published still reports what it actually asks.
  const questionCount = instrument ? questions.length : poll.currentVersion?.questionCount ?? 0
  // `editable` is the SERVER's answer, not `stateCode !== 'PUBLISHED'` recomputed here.
  const published = !!poll.currentVersion && !poll.currentVersion.editable

  /**
   * The things that are wrong TOGETHER.
   *
   * Deliberately short and deliberately not a copy of the server's checks: the
   * lifecycle refuses what it refuses and says so in its own words. These are
   * the cross-step readings a person would otherwise only make by clicking back
   * through four panels.
   */
  const observations: string[] = []
  if (!published) {
    observations.push('The instrument has no published version yet, so this poll cannot go '
      + 'live and its next wave would have nothing to copy.')
  }
  if (questionCount === 0) {
    observations.push('The instrument has no questions.')
  }
  if (sample && sample.adequacyPct < 100) {
    observations.push(`The target of ${count(sample.targetSample)} interviews is `
      + `${sample.adequacyPct}% of what the declared ±${num(sample.marginOfErrorPct, 1)}pp `
      + `needs. It will still run; it cannot claim that precision.`)
  }
  if (sampleError) {
    observations.push(sampleError)
  }
  if (poll.categoryCode === 'TRACKER' && !poll.groupId) {
    observations.push('This is filed as a tracker but belongs to no tracker, so nothing will '
      + 'line its waves up. Join one on step 1.')
  }
  // ASSIGNED or IN_PROGRESS is what the server's guard counts; a revoked or completed
  // assignment leaves nobody in the field.
  const staffed = (assignments ?? []).some(
    (a) => a.statusCode === 'ASSIGNED' || a.statusCode === 'IN_PROGRESS')
  if (assignments !== null && !staffed && poll.statusCode !== 'LIVE'
    && poll.statusCode !== 'CLOSED' && poll.statusCode !== 'ARCHIVED') {
    observations.push('Nobody is assigned to field this poll, and it cannot go live until '
      + 'somebody is — a live poll with no interviewer collects nothing while its progress '
      + 'reads 0 of ' + poll.targetSample + ' indefinitely. Staffing is on the Fieldwork '
      + 'screen, linked below.')
  }
  if (schedule?.recurring && !schedule.autoWave) {
    observations.push(`The frequency says ${frequencyLabel(schedule.frequencyCode).toLowerCase()}, `
      + 'but automatic waves are off — each wave will have to be opened by hand.')
  }

  return (
    <div className="space-y-4">
      <Panel title="Review" description="Every decision on one screen, before it is submitted">
        <div className="space-y-4">
          <ErrorStrip message={error} />

          <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Row label="Reference" value={poll.ref} />
            <Row label="Status" value={pollStatusLabel(poll.statusCode)} />
            <Row label="Title" value={poll.title} />
            <Row label="Scope" value={poll.scopeUnitLabels?.length
              ? poll.scopeUnitLabels.join(' › ') : poll.scopeUnitName ?? '—'} />
            <Row label="Category" value={categoryLabel(poll.categoryCode)} />
            <Row label="Purpose" value={purposeLabel(poll.purposeCode)} />
            <Row label="Audience" value={audienceLabel(poll.audienceCode)} />
            <Row label="Method" value={methodologyLabel(poll.methodologyCode)} />
            <Row label="Tracker" value={poll.groupName
              ? `${poll.groupName}${poll.waveNo ? ` · wave ${poll.waveNo}` : ''}`
              : 'Standalone'} />
            <Row label="Frequency" value={frequencyLabel(poll.frequencyCode)} />
            <Row label="Instrument" value={poll.currentVersion
              ? `Version ${poll.currentVersion.versionNo} · ${questionCount} question`
                + `${questionCount === 1 ? '' : 's'} · ${published ? 'published' : 'draft'}`
              : 'No version'} />
            <Row label="Fieldwork window" value={poll.opensAt || poll.closesAt
              ? `${poll.opensAt ? fmtDateTime(poll.opensAt) : 'any time'} → `
                + `${poll.closesAt ? fmtDateTime(poll.closesAt) : 'no end'}`
              : 'Not set'} />
          </div>

          <div className="border-t border-border pt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Row label="Target sample" value={count(poll.targetSample)} />
            <Row label="Declared precision" value={poll.targetMoe == null
              ? 'None declared'
              : `±${num(poll.targetMoe, 1)}pp at ${poll.confidenceLevel}%`} />
            <Row label="Sample-size verdict" value={sample
              ? `${count(sample.requiredN)} needed · ${sample.adequacyPct}% adequate`
              : sampleError ? 'Cannot be computed' : '…'} />
            <Row label="Sampling design" value={readiness
              ? `${count(readiness.strataCount)} strat${readiness.strataCount === 1 ? 'um' : 'a'}`
                + ` · ${count(readiness.quotaCellCount)} quota cell`
                + `${readiness.quotaCellCount === 1 ? '' : 's'}`
                + ` · frame ${readiness.populationFrameDeclared ? 'declared' : 'missing'}`
              : '…'} />
            <Row label="Automatic waves" value={schedule
              ? schedule.autoWave
                ? `On · next ${schedule.nextRunAt ? fmtDateTime(schedule.nextRunAt) : '—'}`
                : 'Off'
              : '…'} />
            <Row label="Links" value={links === null ? '…' : links.length === 0 ? 'None'
              : links.map((l) => `${linkTypeLabel(l.typeCode)}: ${l.label ?? l.ref}`).join(', ')} />
          </div>

          {observations.length > 0 && (
            <div className="space-y-1.5 border-t border-border pt-3">
              <p className="text-erp-xs font-medium text-muted-foreground">
                Worth reading before this goes anywhere
              </p>
              {observations.map((o) => <Note key={o}>{o}</Note>)}
            </div>
          )}
          {observations.length === 0 && (
            <Note tone="good">
              Nothing on these steps contradicts anything else. The lifecycle still has its own
              guards, and it will say so in its own words if one of them refuses.
            </Note>
          )}
        </div>
      </Panel>

      {/*
        THE INSTRUMENT, IN FULL.
 
        A review screen that said "12 questions" and showed none of them is not a
        review — the thing being approved is the wording, and the wording was the
        one part of the poll this step did not print. Every question, in position
        order, with its options and what each is linked to.
      */}
      <Panel
        title="The instrument"
        description={instrument
          ? `Version ${instrument.version.versionNo}`
            + ` · ${instrument.version.editable ? 'draft' : 'published'}`
            + ` · ${count(questions.length)} question${questions.length === 1 ? '' : 's'}`
          : 'What this poll asks'}
        right={(
          <Link to={`/polling/configure?poll=${poll.id}&step=instrument`}
            className="erp-btn-ghost h-8 text-erp-xs inline-flex items-center">
            Edit
          </Link>
        )}
      >
        {instrument === null ? <LoadingBlock label="Reading the instrument…" />
          : questions.length === 0 ? (
            <Note>
              This instrument has no questions. A poll cannot be published — and so cannot go
              live — until it asks something, because a published empty version would satisfy
              the LIVE guard and then accept interviews containing nothing.
            </Note>
          ) : (
            <ol className="space-y-3">
              {questions.map((q) => (
                <li key={q.id} className="border border-border rounded-md p-3">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-erp-xs font-mono text-muted-foreground">
                      {q.position}. {q.code}
                    </span>
                    <span className="text-erp-sm font-medium min-w-0">{q.text}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                    <Pill>{questionTypeLabel(q.typeCode)}</Pill>
                    {q.required ? <Pill tone="bg-amber-500/10 text-amber-700 border-amber-500/30">Required</Pill> : null}
                    {q.blockCode ? <Pill>{q.blockCode}</Pill> : null}
                    {q.linkedEntity && (
                      <Pill tone={q.linkedEntity.resolved
                        ? 'bg-sky-500/10 text-sky-700 border-sky-500/30'
                        : 'bg-red-500/10 text-red-700 border-red-500/30'}>
                        {linkTypeLabel(q.linkedEntity.typeCode)}:{' '}
                        {q.linkedEntity.resolved
                          ? q.linkedEntity.label
                          : 'cannot be named'}
                      </Pill>
                    )}
                  </div>
                  {q.helpText && (
                    <p className="text-erp-xs text-muted-foreground mt-1.5">{q.helpText}</p>
                  )}
                  {q.options.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {[...q.options].sort((a, b) => a.position - b.position).map((o) => (
                        <li key={o.id} className="text-erp-xs flex items-baseline gap-2">
                          <span className="font-mono text-muted-foreground w-20 shrink-0 truncate">
                            {o.code}
                          </span>
                          <span className="min-w-0">{o.label}</span>
                          {o.kindCode !== 'SUBSTANTIVE' && (
                            <span className="text-muted-foreground font-mono">{o.kindCode}</span>
                          )}
                          {o.linkedEntity && (
                            <span className={o.linkedEntity.resolved
                              ? 'text-sky-700' : 'text-red-700'}>
                              → {o.linkedEntity.resolved
                                ? o.linkedEntity.label : 'link cannot be named'}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          )}
        {instrument && instrument.skipRules.length > 0 && (
          <p className="text-erp-xs text-muted-foreground mt-3">
            {count(instrument.skipRules.length)} branch
            {instrument.skipRules.length === 1 ? '' : 'es'} — the outline above is what an
            interviewer sees before any of them fire. The Builder previews them against real
            answers.
          </p>
        )}
      </Panel>

      <Panel title="What happens next"
        description="The edges the server will accept from here, for you">
        <div className="space-y-3">
          {poll.allowedTransitions.length === 0 ? (
            <p className="text-erp-sm text-muted-foreground">
              There is nowhere to move this poll from {pollStatusLabel(poll.statusCode)}.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {poll.allowedTransitions.map((status) => (
                <button key={status} type="button"
                  className="erp-btn-ghost h-9 text-erp-sm"
                  disabled={!canUpdate || busy}
                  onClick={() => move(status)}>
                  Move to {pollStatusLabel(status)}
                </button>
              ))}
            </div>
          )}
          <p className="text-erp-xs text-muted-foreground">
            This list is the server's, not this screen's. A hardcoded "Submit for review" would
            offer an edge the state machine may refuse — or hide one it would have allowed.
          </p>
          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            <Link to={`/polling/configure?poll=${poll.id}&step=instrument`}
              className="erp-btn-ghost h-8 text-erp-xs">
              Instrument
            </Link>
            <Link to={`/polling/sample?poll=${poll.id}`} className="erp-btn-ghost h-8 text-erp-xs">
              Sampling design
            </Link>
            <Link to={`/polling/fieldwork?poll=${poll.id}`}
              className={assignments !== null && !staffed
                ? 'erp-btn-primary h-8 text-erp-xs inline-flex items-center'
                : 'erp-btn-ghost h-8 text-erp-xs inline-flex items-center'}>
              {assignments !== null && !staffed ? 'Staff it' : 'Staffing'}
            </Link>
          </div>
        </div>
      </Panel>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-erp-xs text-muted-foreground">{label}</p>
      <p className="text-erp-sm break-words">{value}</p>
    </div>
  )
}
